import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../lib/api";
import { log } from "../lib/logger";

const AuthCtx = createContext(null);

/**
 * Auth is cookie-based (httpOnly `mazad_access` JWT + non-httpOnly `mazad_csrf` sibling).
 * The React app never sees the JWT directly; it just calls `/api/auth/me` on mount and
 * lets `withCredentials: true` on axios do the rest. The `mazad_token` localStorage key is
 * no longer written — any residue from an old session is cleared on mount.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      if (err?.response?.status !== 401) log.warn("[AuthContext] /auth/me failed:", err);
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    // Sweep away legacy localStorage token so it never re-authenticates by accident.
    try { localStorage.removeItem("mazad_token"); }
    catch (err) { log.warn("[AuthContext] localStorage cleanup failed:", err); }
    // Google OAuth callback lives on the hash — skip /me while that runs.
    if (typeof window !== "undefined" && window.location.hash.includes("session_id=")) {
      setLoading(false);
      return;
    }
    load();
  }, [load]);

  const signin = async (identifier, password) => {
    const { data } = await api.post("/auth/login", { identifier, password });
    if (data && data.two_factor_required) {
      return { two_factor_required: true, mfa_token: data.mfa_token };
    }
    // Cookies are set by the server; we just capture the user.
    setUser(data.user);
    return data.user;
  };

  const complete2FALogin = async (mfa_token, code) => {
    const { data } = await api.post("/auth/2fa/login", { mfa_token, code });
    setUser(data.user);
    return data.user;
  };

  const signup = async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    setUser(data.user);
    return data.user;
  };

  // Kept for the Google OAuth callback which still returns a user + token payload.
  // Token is now ignored (server also set the httpOnly cookie in the same response),
  // but the callback signature stays the same to avoid touching that flow.
  const setToken = (_token, u) => {
    setUser(u);
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Even if the network call fails, drop the local user state.
      log.warn("[AuthContext] logout network call failed:", err);
    }
    setUser(null);
  };

  const refresh = load;

  const value = useMemo(
    () => ({ user, loading, signin, complete2FALogin, signup, logout, setToken, refresh }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
